import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, BookOpen, ArrowRight, Trophy, Clock } from 'lucide-react';
import ActiveScopeChip from '@/components/ActiveScopeChip';

export default function LibraryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quizHistory, setQuizHistory] = useState([]);
  const [flashcardSets, setFlashcardSets] = useState([]);
  // Phase 2.70 — scope chip. Snapshots localStorage so a top-bar change
  // mid-browse doesn't pull lists out from under the student. Clear
  // filter reverts to the full library locally.
  const [scopeSubject, setScopeSubject] = useState(() => {
    try { return window.localStorage.getItem('gv_active_subject') || ''; }
    catch { return ''; }
  });
  const [scopeTopic, setScopeTopic] = useState(() => {
    try { return window.localStorage.getItem('gv_active_topic') || ''; }
    catch { return ''; }
  });

  useEffect(() => {
    const parts = [];
    if (scopeSubject) parts.push(`subject=${encodeURIComponent(scopeSubject)}`);
    if (scopeTopic) parts.push(`topic=${encodeURIComponent(scopeTopic)}`);
    const qs = parts.length ? `?${parts.join('&')}` : '';
    apiGet(`/quiz/history${qs}`).then(d => setQuizHistory((d.quizzes || []).slice(0, 3))).catch(() => {});
    apiGet(`/flashcards/sets${qs}`).then(d => setFlashcardSets((d.sets || []).slice(0, 3))).catch(() => {});
  }, [scopeSubject, scopeTopic]);

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 pb-4" data-testid="library-page">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">Library</h1>

      {/* Phase 2.70 — chapter-scope chip */}
      <ActiveScopeChip
        subject={scopeSubject}
        topic={scopeTopic}
        onClear={() => { setScopeSubject(''); setScopeTopic(''); }}
      />

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3 mb-8 stagger-children">
        <button data-testid="library-quiz-btn" onClick={() => navigate('/quiz')} className="text-left">
          <Card className="p-5 rounded-2xl border-0 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/20 hover:-translate-y-1 transition-all duration-200 active:scale-95">
            <Brain className="w-7 h-7 mb-3 opacity-90" />
            <p className="font-heading font-semibold text-sm">Take a Quiz</p>
            <p className="text-xs opacity-80 font-body mt-0.5">Test your knowledge</p>
          </Card>
        </button>
        <button data-testid="library-flashcards-btn" onClick={() => navigate('/flashcards')} className="text-left">
          <Card className="p-5 rounded-2xl border-0 bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/20 hover:-translate-y-1 transition-all duration-200 active:scale-95">
            <BookOpen className="w-7 h-7 mb-3 opacity-90" />
            <p className="font-heading font-semibold text-sm">Flashcards</p>
            <p className="text-xs opacity-80 font-body mt-0.5">Revise concepts</p>
          </Card>
        </button>
      </div>

      {/* Recent Quizzes */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider">Recent Quizzes</h3>
          {quizHistory.length > 0 && (
            <button data-testid="view-all-quizzes" onClick={() => navigate('/quiz')} className="text-xs text-blue-500 font-body flex items-center gap-1">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
        {quizHistory.length === 0 ? (
          <p className="text-sm text-slate-400 font-body py-4 text-center">No quizzes taken yet. Start your first one!</p>
        ) : (
          <div className="space-y-2">
            {quizHistory.map(q => (
              <Card key={q.quiz_id} className="p-4 rounded-2xl border-slate-100" data-testid={`quiz-history-${q.quiz_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading text-sm font-semibold text-slate-800">{q.topic}</p>
                    <p className="text-xs text-slate-500 font-body">{q.subject}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${q.score / q.total >= 0.7 ? 'bg-emerald-50 text-emerald-600' : q.score / q.total >= 0.4 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'} text-xs`}>
                      {q.score}/{q.total}
                    </Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Recent Flashcard Sets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider">Recent Flashcards</h3>
          {flashcardSets.length > 0 && (
            <button data-testid="view-all-flashcards" onClick={() => navigate('/flashcards')} className="text-xs text-blue-500 font-body flex items-center gap-1">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
        {flashcardSets.length === 0 ? (
          <p className="text-sm text-slate-400 font-body py-4 text-center">No flashcard sets yet. Create your first!</p>
        ) : (
          <div className="space-y-2">
            {flashcardSets.map(s => (
              <Card key={s.set_id} className="p-4 rounded-2xl border-slate-100" data-testid={`fc-set-${s.set_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading text-sm font-semibold text-slate-800">{s.topic}</p>
                    <p className="text-xs text-slate-500 font-body">{s.subject}</p>
                  </div>
                  <Badge className="bg-amber-50 text-amber-600 text-xs">{s.cards?.length} cards</Badge>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
